import { DateTime } from 'luxon';

/** Comment rows mirror these event types – hide events from feed (FP: activity = comments only). */
const COMMENT_MIRROR_EVENT_TYPES = new Set([
  'comment.created',
  'comment.updated',
  'participant.added',
  'participant.removed',
  'department.added',
  'department.removed',
  'approval.approver_set',
  'approval.approved',
  'approval.rejected',
  'execution.state_changed',
  'execution.completed',
  'execution.assignee_set',
  'execution.step_set',
  'next_step.set',
  'inspection.inspector_set',
  'inspection.state_changed',
  'quote_extension.upserted',
  'quote_followup.created',
  'announcement.sent',
  'semaphore.state_changed',
]);

/** Always hidden in user-facing activity (audit-only or shown elsewhere). */
const HIDDEN_ACTIVITY_EVENT_TYPES = new Set([
  'ticket.created',
  'ticket.updated',
  'attachment.added',
  'attachment.removed',
  'task.created',
  'task.updated',
  'task.deleted',
]);

/** Events shown in feed when there is no system comment twin (parita FP: uzavretie len v komentároch). */
const VISIBLE_ACTIVITY_EVENT_TYPES = new Set([
  'semaphore.state_changed',
]);

/**
 * @param {string} type
 * @returns {boolean}
 */
export function isCrmActivityEventVisible(type) {
  const normalizedType = String(type || '');
  if (COMMENT_MIRROR_EVENT_TYPES.has(normalizedType) || HIDDEN_ACTIVITY_EVENT_TYPES.has(normalizedType)) {
    return false;
  }

  return VISIBLE_ACTIVITY_EVENT_TYPES.has(normalizedType);
}

/**
 * @typedef {'event'|'comment'} ActivityItemKind
 * @typedef {{ kind: 'event', id: string, at: string, event: Record<string, unknown> }} ActivityEventItem
 * @typedef {{ kind: 'comment', id: string, at: string, comment: Record<string, unknown> }} ActivityCommentItem
 * @typedef {ActivityEventItem|ActivityCommentItem} ActivityItem
 */

/**
 * @param {Array<Record<string, unknown>>|null|undefined} events
 * @param {Array<Record<string, unknown>>|null|undefined} comments
 * @returns {ActivityItem[]}
 */
export function buildCrmActivityItems(events, comments) {
  const items = [];

  for (const row of Array.isArray(events) ? events : []) {
    const type = String(row?.type || '');
    if (!isCrmActivityEventVisible(type)) {
      continue;
    }
    const at = String(row?.created_at || '');
    items.push({
      kind: 'event',
      id: `event-${row?.id ?? type}-${at}`,
      at,
      event: row,
    });
  }

  for (const row of Array.isArray(comments) ? comments : []) {
    const at = String(row?.updated_at || row?.created_at || '');
    items.push({
      kind: 'comment',
      id: `comment-${row?.id}`,
      at,
      comment: row,
    });
  }

  return items;
}

/**
 * @param {ActivityItem[]} items
 * @param {boolean} sortDescending newest first when true
 * @returns {ActivityItem[]}
 */
export function sortCrmActivityItems(items, sortDescending) {
  return items.slice().sort((a, b) => {
    const ta = DateTime.fromISO(String(a.at || '')).toMillis() || 0;
    const tb = DateTime.fromISO(String(b.at || '')).toMillis() || 0;
    return sortDescending ? tb - ta : ta - tb;
  });
}
